import React from "react";
import { Link } from "react-router-dom";
import { createPageStyles } from "./MarketingPageStyles";
import { detectPageLocale } from "./pageLocale";

const styles = createPageStyles("light", "veil");

type Locale = "ko" | "en";
type Row = [string, string, string, string];

const LOCALE_STORAGE_KEY = "veil-privacy-locale";
const locales: Locale[] = ["ko", "en"];
const localeLabels: Record<Locale, string> = { ko: "KO", en: "EN" };

const tableRows: Record<Locale, Row[]> = {
  ko: [
    ["사진과 보정 작업", "불러오거나 촬영한 사진의 앱 내부 복사본, 보정값, 프레임 문구, 프리셋, 최근 초안, 저장 히스토리, 필름 롤", "사진 편집·저장, 재편집, 히스토리 표시", "사용자 기기 내 (앱 로컬 저장소)"],
    ["앱 설정", "저장 품질, 언어, 온보딩 완료 여부", "설정 유지", "사용자 기기 내 (앱 로컬 저장소)"],
    ["인앱 구매 (Veil Pro)", "앱은 결제정보를 수집하지 않음 (구매 영수증과 구독 상태는 Apple이 처리)", "Pro 이용 상태 확인 및 복원", "Apple App Store"],
  ],
  en: [
    ["Photos and edits", "In-app copies of photos you import or capture, adjustment values, frame captions, presets, the latest draft, save history, film rolls", "Editing and saving photos, re-editing, showing history", "On device (local app storage)"],
    ["App settings", "Save quality, language, onboarding completion", "Keeping your settings", "On device (local app storage)"],
    ["In-app purchase (Veil Pro)", "The app does not collect payment data (receipts and subscription status are handled by Apple)", "Verifying and restoring Pro status", "Apple App Store"],
  ],
};

const content: Record<
  Locale,
  {
    crumb: string;
    title: string;
    accentTitle: string;
    subtitle: string;
    tableHead: Row;
    sections: Array<{ title: string; body?: string; list?: string[] }>;
    websiteStats: { title: string; body: string; list: string[] };
  }
> = {
  ko: {
    crumb: "Veil Privacy",
    title: "Veil,",
    accentTitle: "개인정보 처리방침",
    subtitle:
      "최종 업데이트일: 2026년 10월 29일. 본 정책의 운영 주체는 Daydream Factory입니다. Veil은 회원가입 없이 사용할 수 있으며, 사진과 보정 작업은 모두 사용자 기기 안에서 처리되고 저장됩니다. 사진을 서버로 전송하지 않으며, 별도의 서버 계정에 개인정보를 수집하지 않습니다.",
    tableHead: ["구분", "수집 항목", "사용 목적", "저장 위치"],
    sections: [
      { title: "1. 처리하는 정보" },
      {
        title: "2. 기기 권한",
        list: [
          "카메라: 장면 카메라와 필름 롤 촬영에 사용합니다. 촬영 전 권한을 요청하며, 거부해도 사진 불러오기와 편집은 사용할 수 있습니다.",
          "사진 보관함: 편집할 사진을 고르고, 완성한 사진을 보관함에 저장하는 데 사용합니다. 선택한 사진만 앱 내부로 복사합니다.",
          "권한은 기기의 시스템 설정에서 언제든 변경할 수 있습니다.",
        ],
      },
      {
        title: "3. 저장되는 사진과 메타데이터",
        list: [
          "저장한 사진은 새 파일로 만들어지며, 촬영 위치(GPS) 등 원본의 메타데이터는 결과 파일에 포함하지 않습니다.",
          "원본 사진은 수정하지 않습니다. 다시 편집할 수 있도록 원본의 복사본과 보정값을 앱 내부에 보관합니다.",
          "사진은 기기에서만 처리합니다. 분석, 광고, 학습 목적으로 외부에 전송하지 않습니다.",
        ],
      },
      {
        title: "4. 제3자 처리 위탁",
        body: "Veil은 광고·분석 SDK를 사용하지 않습니다. 아래 서비스만 결제 처리에 관여하며, 해당 서비스의 개인정보 처리방침이 함께 적용됩니다.",
        list: ["Apple App Store / Google Play"],
      },
      {
        title: "5. 데이터 보관 및 삭제",
        list: [
          "사진·보정값·히스토리·프리셋·필름 롤은 기기에만 저장되며, 앱을 삭제하면 함께 삭제됩니다.",
          "최근 초안은 설정에서, 히스토리 항목은 히스토리 화면에서 개별 삭제할 수 있습니다.",
          "사진 보관함에 저장한 결과 파일은 앱을 삭제해도 남아 있으며, 기기의 사진 앱에서 관리합니다.",
        ],
      },
      {
        title: "6. 이용자 권리 및 문의",
        body:
          "이용자는 언제든 앱 삭제로 앱 내부 데이터를 삭제할 수 있습니다. 개인정보 관련 문의: support@daydreamfactory.net",
      },
    ],
    websiteStats: {
      title: "7. 웹사이트 정보",
      body: "본 페이지(daydreamfactory.net)는 방문 통계 측정을 위해 익명화된 분석 도구를 사용할 수 있습니다.",
      list: ["수집 항목: 페이지 조회, 익명 기기/브라우저 정보", "목적: 사이트 개선"],
    },
  },
  en: {
    crumb: "Veil Privacy",
    title: "Veil,",
    accentTitle: "Privacy Policy",
    subtitle:
      "Last updated: October 29, 2026. This policy is operated by Daydream Factory. Veil can be used without an account, and your photos and edits are processed and stored entirely on your device. Photos are never uploaded to a server, and we do not collect personal data into a separate server account.",
    tableHead: ["Category", "Items", "Purpose", "Storage location"],
    sections: [
      { title: "1. Information We Process" },
      {
        title: "2. Device Permissions",
        list: [
          "Camera: used for the scene camera and film roll capture. Permission is requested before shooting; if declined, importing and editing photos still work.",
          "Photo library: used to pick photos to edit and to save finished photos. Only the photos you select are copied into the app.",
          "You can change permissions at any time in your device's system settings.",
        ],
      },
      {
        title: "3. Saved Photos and Metadata",
        list: [
          "Saved photos are written as new files. Original metadata such as capture location (GPS) is not included in the output file.",
          "Original photos are never modified. A copy of the original and your adjustment values are kept inside the app so you can re-edit later.",
          "Photos are processed on device only. They are not sent anywhere for analytics, advertising, or training.",
        ],
      },
      {
        title: "4. Third-Party Processing",
        body: "Veil does not use advertising or analytics SDKs. Only the services below are involved, for billing, and their own privacy policies also apply.",
        list: ["Apple App Store / Google Play"],
      },
      {
        title: "5. Data Retention and Deletion",
        list: [
          "Photos, adjustments, history, presets, and film rolls are stored only on your device and are deleted when you delete the app.",
          "The latest draft can be deleted in Settings, and history items can be deleted individually from the History screen.",
          "Result files saved to your photo library remain after the app is deleted and are managed in your device's Photos app.",
        ],
      },
      {
        title: "6. Your Rights and Contact",
        body:
          "You can delete all in-app data at any time by deleting the app. Privacy inquiries: support@daydreamfactory.net",
      },
    ],
    websiteStats: {
      title: "7. Website Information",
      body: "This page (daydreamfactory.net) may use anonymized analytics to measure visit statistics.",
      list: ["Collected: page views, anonymous device/browser information", "Purpose: site improvement"],
    },
  },
};

const VeilPrivacyPolicy: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("en");

  React.useEffect(() => {
    setLocale(detectPageLocale(LOCALE_STORAGE_KEY, locales, "en"));
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const copy = content[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.homeLink}>
            ← Daydream Factory
          </Link>
          <div style={styles.topRowMeta}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {locales.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLocale(item)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === item ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[item]}
                </button>
              ))}
            </div>
            <span style={styles.crumb}>{copy.crumb}</span>
          </div>
        </div>

        <section style={styles.hero}>
          <h1 style={styles.title}>
            {copy.title}
            <br />
            <span style={styles.accentTitle}>{copy.accentTitle}</span>
          </h1>
          <p style={styles.subtitle}>{copy.subtitle}</p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>{copy.sections[0].title}</h2>
          <div style={styles.sectionCard}>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {copy.tableHead.map((item) => (
                      <th key={item} style={styles.thtd}>
                        {item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows[locale].map((row) => (
                    <tr key={row[0]}>
                      {row.map((cell) => (
                        <td key={cell} style={styles.thtd}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {copy.sections.slice(1).map((section) => (
          <section key={section.title} style={styles.section}>
            <h2 style={styles.h2}>{section.title}</h2>
            <div style={styles.sectionCard}>
              {section.body ? (
                <p style={styles.body}>
                  {section.body.includes("support@daydreamfactory.net") ? (
                    <>
                      {section.body.replace("support@daydreamfactory.net", "")}
                      <a href="mailto:support@daydreamfactory.net" style={styles.link}>
                        support@daydreamfactory.net
                      </a>
                    </>
                  ) : (
                    section.body
                  )}
                </p>
              ) : null}
              {section.list ? (
                <ul style={styles.list}>
                  {section.list.map((item) => (
                    <li key={item}>
                      {item === "Apple App Store / Google Play" ? (
                        <>
                          Apple App Store / Google Play -{" "}
                          <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noreferrer" style={styles.link}>
                            Apple Privacy Policy
                          </a>
                        </>
                      ) : (
                        item
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        ))}

        <section style={styles.section}>
          <h2 style={styles.h2}>{copy.websiteStats.title}</h2>
          <div style={styles.sectionCard}>
            <p style={styles.body}>{copy.websiteStats.body}</p>
            <ul style={styles.list}>
              {copy.websiteStats.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </main>
  );
};

export default VeilPrivacyPolicy;
